<template>
  <div class="rangeSelect pt-4">
    <div class="rangeSelect-wrp" :style="{ gridTemplateColumns: `repeat(${5}, 1fr)` }">
      <div
        v-for="(el, index) in optionsLength"
        :key="el.level"
        class="rangeSelect-el"
        :class="{
          level0: index === 2,
          levelBord1: index === 1,
          levelBord2: index === 3,
          redLevel1: reactionsLevel === -1 && optionsLength[index].value === -1,
          redLevel2: reactionsLevel === -2 && optionsLength[index].value === -2,
          greenLevel1: reactionsLevel === 1 && optionsLength[index].level === 3,
          greenLevel2: reactionsLevel === 2 && optionsLength[index].level >= 4,
        }"
        @click="handleSetValue(el.value, el.level)">
        <div class="rangeSelect-el-line" :class="`color-${Math.abs(el.value)}`"></div>
        <div
          class="rangeSelect-el-txt"
          :class="
            clsx(
              {
                selected: model >= Math.abs(el.value),
              },
              `color-${el.level}`,
            )
          ">
          {{ Math.abs(el.value) }}
        </div>
      </div>
    </div>
    
    <span class="rangeSelect-el-circle" :class="`color-${model}`" :style="{ left: positionBullet }"></span>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import clsx from 'clsx';

const optionsLength = [
  { level: 0, value: -2 },
  { level: 1, value: -1 },
  { level: 2, value: 0 },
  { level: 3, value: 1 },
  { level: 4, value: 2 },
];
// Positions for the circle (center of each section)
const positionsBullet = [10, 30, 50, 70, 90];

const reactionsLevel = ref(0);
const model = defineModel();
const isRed = ref(false);

// Initialize level based on model value
const level = ref(2); // Default to neutral (level 2)

// Update level when model changes
watch(model, (newValue) => {
  // Find the matching option based on the model value
  const matchingOption = optionsLength.find(opt => opt.value === newValue);
  if (matchingOption) {
    level.value = matchingOption.level;
    reactionsLevel.value = newValue;
  }
}, { immediate: true });

const positionBullet = computed(() => {
  if (level.value < 2) {
    isRed.value = true;
  } else {
    isRed.value = false;
  }

  // Move the slider (circle)
  if (level.value >= 0 && level.value < positionsBullet.length) {
    return positionsBullet[level.value] + '%';
  }
  return '0px';
});

function handleSetValue(value, position) {
  reactionsLevel.value = value;
  level.value = position;
  model.value = value;
}
</script>

<style scoped lang="scss">
.rangeSelect {
  position: relative;

  &-wrp {
    display: grid;
    width: 100%;
  }

  &.isRed {
    --color: #931919;
    --color_2: #b72020;
    --color_3: #dc2626;
    --borderSelected: #e24a4a;
    --colorHovered_1: #715353;
    --colorHovered_2: #835656;
  }

  &.isGreen {
    --color: #17833f;
    --color_2: #1ca44e;
    --color_3: #22c55e;
    --borderSelected: #035b21;
    --colorHovered_1: #526d5c;
    --colorHovered_2: #547660;
  }

&.hasSelected {
  .rangeSelect-el-line {
    background: var(--colorHovered);
    border-color: var(--colorHovered);
    border-left-color: #959595;
  }
}

&-el {
    cursor: pointer;
    position: relative;
    &.level0 .rangeSelect-el-line {
      border: 2px solid #1d1d21;
      border-radius: 10px;
      height: 14px;
      margin-top: 13px; /* Adjust to match other lines */
    }
    &.levelBord1 .rangeSelect-el-line {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }
    &.levelBord2 .rangeSelect-el-line {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }
    &.redLevel1 .rangeSelect-el-line {
      background-color: #931919;
      border-color: #931919;
      box-shadow: 0 0 7px #931919;
    }
    &.redLevel2 .rangeSelect-el-line {
      background-color: #b72020;
      border-color: #b72020;
      box-shadow: 0 0 7px #b72020;
    }
    &.redLevel3 .rangeSelect-el-line {
      background-color: #dc2626;
      border-color: #dc2626;
      box-shadow: 0 0 7px #dc2626;
    }
    &.greenLevel1 .rangeSelect-el-line {
      background-color: #17833f;
      border-color: #17833f;
      box-shadow: 0 0 7px #17833f;
    }
    &.greenLevel2 .rangeSelect-el-line {
      background-color: #1ca44e;
      border-color: #1ca44e;
      box-shadow: 0 0 7px #1ca44e;
    }
    &.greenLevel3 .rangeSelect-el-line {
      background-color: #22c55e;
      border-color: #22c55e;
      box-shadow: 0 0 7px #22c55e;
    }

    &-circle {
      border: 2px solid #fff;
      width: 14px; /* A bit larger for better visibility */
      pointer-events: none;
      border-radius: 50%;
      background: var(--color);
      position: absolute;
      top: 37px;
      height: 14px; /* A bit larger for better visibility */
      transition: left 0.3s ease-in-out;
      z-index: 2;
      box-shadow: 0 0 5px #fff;
      transform: translateX(-50%) translateY(-50%); /* Center the circle horizontally and vertically */
    }

    &-txt {
      text-align: center;
      margin-top: 4px;
      font-size: 12px;
      color: #a1a1aa;
      transition: 0.2s;
    }

    &:nth-child(1) {
      .rangeSelect-el-line {
        border-radius: 40px 0 0 40px;
        border-left: none;
      }
    }

    &:last-child {
      .rangeSelect-el-line {
        border-radius: 0 40px 40px 0;
      }
    }

    &-line {
      background: #464648;
      border: 2px solid #464648;
      height: 10px;
      position: relative;
      z-index: 0;
      transition: 0.15s;
      margin-top: 15px; /* Position the line to align with the circle */
    }

    &.selected {
      .rangeSelect-el-line {
        box-shadow: 0 0 7px var(--colorSelected);
        background: var(--colorSelected);
        border: 2px solid var(--colorSelected);
      }
    }

    &.active {
      .rangeSelect-el-line {
        background: var(--color);
      }
    }
  }
}
</style>
